import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Validation types
interface ValidationState {
  isValid: boolean;
  message: string;
}

interface FormValidation {
  email: ValidationState;
  password: ValidationState;
}

interface LoginData {
  email: string;
  password: string;
}

interface ApiError {
  field: string;
  message: string;
}

interface LoginUser {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified: boolean;
  phone_verified: boolean;
}

interface ApiResponse {
  status: string;
  message?: string;
  user_id?: string;
  user?: LoginUser;
  errors?: ApiError[];
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Validation state
  const [validation, setValidation] = useState<FormValidation>({
    email: { isValid: true, message: '' },
    password: { isValid: true, message: '' },
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Validation functions
  const validateEmail = (email: string): ValidationState => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      return { isValid: false, message: 'Email is required' };
    }
    
    if (trimmedEmail.length > 254) {
      return { isValid: false, message: 'Email must be 254 characters or less' };
    }
    
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(trimmedEmail)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    
    return { isValid: true, message: '' };
  };

  const validatePassword = (password: string): ValidationState => {
    if (!password) {
      return { isValid: false, message: 'Password is required' };
    }
    
    return { isValid: true, message: '' };
  };

  // Real-time validation handlers
  const handleEmailChange = (value: string) => {
    setEmail(value);
    const validation = validateEmail(value);
    setValidation(prev => ({ ...prev, email: validation }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const validation = validatePassword(value);
    setValidation(prev => ({ ...prev, password: validation }));
  };

  // Check if form is valid
  const isFormValid = () => {
    return validation.email.isValid && 
           validation.password.isValid &&
           email.trim() &&
           password.trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setShowError(true);
      setErrorMessage('Please fix the validation errors before submitting');
      return;
    }
    
    setIsLoading(true);
    setShowError(false);
    setShowSuccess(false);
    
    try {
      const loginData: LoginData = {
        email: email.toLowerCase().trim(),
        password: password,
      };

      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data: ApiResponse = await response.json();

      if (response.ok && data.status === 'success') {
        setShowSuccess(true);
        setSuccessMessage(data.message || 'Login successful! Redirecting...');
        
        // Update auth context with user data
        if (data.user) {
          login(data.user);
        }
        
        // Clear form
        setEmail('');
        setPassword('');
        
        // Reset validation
        setValidation({
          email: { isValid: true, message: '' },
          password: { isValid: true, message: '' },
        });

        // Redirect to home page after successful login
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        // Handle validation errors from backend
        if (data.errors && data.errors.length > 0) {
          const newValidation = { ...validation };
          data.errors.forEach((error: ApiError) => {
            if (error.field === 'email') {
              newValidation.email = { isValid: false, message: error.message };
            } else if (error.field === 'password') {
              newValidation.password = { isValid: false, message: error.message };
            }
          });
          setValidation(newValidation);
        }
        
        setShowError(true);
        setErrorMessage(data.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setShowError(true);
      setErrorMessage('Unable to connect to the server. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col xs={12} md={8} lg={6}>
          <div className="text-center mb-4">
            <h3 className="display-6 mb-2">🔐 Login</h3>
            <p className="text-muted">Access your Pouncer account</p>
          </div>
          
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              {showSuccess && (
                <Alert variant="success" className="mb-4">
                  <strong>🎉 Success!</strong> {successMessage}
                </Alert>
              )}
              
              {showError && (
                <Alert variant="danger" className="mb-4" data-testid="login-error">
                  <strong>❌ Error:</strong> {errorMessage}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    isValid={email.length > 0 && validation.email.isValid}
                    isInvalid={email.length > 0 && !validation.email.isValid}
                    required
                    data-testid="login-email"
                  />
                  {!validation.email.isValid && (
                    <Form.Control.Feedback type="invalid">
                      {validation.email.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    isValid={password.length > 0 && validation.password.isValid}
                    isInvalid={password.length > 0 && !validation.password.isValid}
                    required
                    data-testid="login-password"
                  />
                  {!validation.password.isValid && (
                    <Form.Control.Feedback type="invalid">
                      {validation.password.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <div className="d-grid mb-3">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    size="lg" 
                    disabled={!isFormValid() || isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </div>
              </Form>
              
              <hr className="my-4" />
              
              <div className="text-center">
                <p className="text-muted mb-2">
                  Don't have an account?
                </p>
                <Link 
                  to="/register"
                  className="btn btn-outline-primary btn-sm"
                >
                  Create Account
                </Link>
              </div>
              
              <div className="text-center mt-3">
                <small className="text-muted">
                  <span className="text-decoration-underline" style={{ color: '#6c757d', cursor: 'pointer' }}>
                    Forgot your password?
                  </span>
                </small>
              </div>
            </Card.Body>
          </Card>
          
          <div className="text-center mt-4">
            <small className="text-muted">
              🔒 Your data is protected with industry-standard security measures
            </small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
